import { useState } from "react";
import { C } from "../../../constants";
import { Ic } from "../../../components/ui/Icons";
import { inputStyle, selectStyle, inputFocus, inputBlur } from "../../../components/ui/Components";
import FileUpload from "../../../components/ui/FileUpload";

function Label({ en, es, required }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.t1 }}>{en}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}</span>
      <span style={{ display: "block", fontSize: 11.5, color: C.t3, fontStyle: "italic" }}>{es}</span>
    </div>
  );
}

function Section({ title, titleEs }) {
  return (
    <div style={{ borderBottom: `2px solid ${C.navy}15`, paddingBottom: 8, marginTop: 32, marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h3>
      {titleEs && <span style={{ fontSize: 12, color: C.t3, fontStyle: "italic" }}>{titleEs}</span>}
    </div>
  );
}

function TextField({ en, es, value, onChange, placeholder, type = "text", required, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en={en} es={es} required={required} />
      {type === "textarea" ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} placeholder={placeholder} style={{ ...inputStyle, resize: "vertical", minHeight: 80, borderColor: error ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ ...inputStyle, borderColor: error ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur} />
      )}
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 2, display: "block" }}>{error}</span>}
    </div>
  );
}

function SelectField({ en, es, value, onChange, options, required, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en={en} es={es} required={required} />
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...selectStyle, borderColor: error ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur}>
        <option value="">-- Select / Seleccionar --</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 2, display: "block" }}>{error}</span>}
    </div>
  );
}

function MultiSelectField({ en, es, value, onChange, options, required, error }) {
  const selected = value ? value.split(", ").filter(Boolean) : [];
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en={en} es={es} required={required} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map(o => {
          const isSelected = selected.includes(o);
          return (
            <button key={o} type="button" onClick={() => {
              const next = isSelected ? selected.filter(x => x !== o) : [...selected, o];
              onChange(next.join(", "));
            }}
              style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${isSelected ? C.navy : error ? C.red : C.border}`, background: isSelected ? `${C.navy}10` : C.white, color: isSelected ? C.navy : C.t2, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: isSelected ? 600 : 400, transition: "all 0.15s" }}>
              {o}
            </button>
          );
        })}
      </div>
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 4, display: "block" }}>{error}</span>}
    </div>
  );
}

function UnitSelector({ value, onChange, required, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en="Unit System" es="Sistema de Unidades" required={required} />
      <div style={{ display: "flex", gap: 8 }}>
        {["Metric (m, m³, °C)", "Imperial (ft, ft³, °F)"].map(o => {
          const isSelected = value === o;
          return (
            <button key={o} type="button" onClick={() => onChange(o)}
              style={{ padding: "8px 18px", borderRadius: 8, border: `1.5px solid ${isSelected ? C.navy : error ? C.red : C.border}`, background: isSelected ? `${C.navy}10` : C.white, color: isSelected ? C.navy : C.t2, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: isSelected ? 600 : 400, transition: "all 0.15s", flex: 1, textAlign: "center" }}>
              {o}
            </button>
          );
        })}
      </div>
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 4, display: "block" }}>{error}</span>}
    </div>
  );
}

export default function EarlyWarningForm({ user, onSubmit, onBack, onComplete }) {
  const [form, setForm] = useState({
    projectName: "", projectLocation: "", endClient: "",
    estimatedClosingDate: "", estimatedExecDate: "",
    detectionObjective: "", sensitivity: "", designStandard: "",
    riskArea: "", riskType: "", estimatedTemp: "",
    environment: "", hasHVAC: "", hvacAirSupplyLocation: "",
    unitSystem: "",
    mainLength: "", mainWidth: "", mainHeight: "",
    falseFloorLength: "", falseFloorWidth: "", falseFloorHeight: "",
    falseCeilingLength: "", falseCeilingWidth: "", falseCeilingHeight: "",
    ceilingType: "", protectionAreas: "",
    needsPanel: "", panelType: "",
    pullStations: "", abortSwitch: "", suppressionRelease: "",
    maintenanceSwitch: "", strobeHorns: "", fireBell: "",
    monitoringModules: "", relayModules: "",
    needsSuppression: "",
    additionalNotes: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [files, setFiles] = useState([]);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const needsPanel = form.needsPanel === "Yes / Sí";
  const hasHVAC = form.hasHVAC === "Yes / Sí";

  const validate = () => {
    const e = {};
    const req = (key, msg) => { if (!form[key] || !form[key].trim()) e[key] = msg || "Required"; };
    req("projectName", "Project name is required");
    req("projectLocation", "Project location is required");
    req("endClient", "End client is required");
    req("estimatedClosingDate", "Closing date is required");
    req("estimatedExecDate", "Execution date is required");
    req("detectionObjective", "Detection objective is required");
    req("designStandard", "Design standard is required");
    req("riskArea", "Risk area is required");
    req("riskType", "Risk type is required");
    req("environment", "Environment type is required");
    req("hasHVAC", "Please indicate HVAC status");
    req("unitSystem", "Please select unit system");
    req("mainLength", "Length is required");
    req("mainWidth", "Width is required");
    req("mainHeight", "Height is required");
    req("protectionAreas", "Please select areas to protect");
    req("needsPanel", "Please indicate if panel is needed");
    if (needsPanel) {
      req("panelType", "Panel type is required");
      req("pullStations", "Enter quantity (0 if none)");
      req("abortSwitch", "Enter quantity (0 if none)");
      req("suppressionRelease", "Enter quantity (0 if none)");
      req("maintenanceSwitch", "Enter quantity (0 if none)");
      req("strobeHorns", "Enter quantity (0 if none)");
      req("fireBell", "Enter quantity (0 if none)");
      req("monitoringModules", "Enter quantity (0 if none)");
      req("relayModules", "Enter quantity (0 if none)");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      onSubmit({ ...form, solutionType: "Design Request: Detection - Early Warning Aspirating", submittedBy: user.name }, files);
      setSubmitting(false);
      setShowSuccess(true);
    }, 800);
  };

  if (showSuccess) {
    return (
      <div style={{ maxWidth: 500, margin: "80px auto", textAlign: "center" }}>
        <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "48px 40px", boxShadow: "0 8px 40px rgba(0,0,0,0.06)" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>✓</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Request Submitted Successfully</h2>
          <p style={{ fontSize: 14, color: C.t2, margin: "0 0 4px", lineHeight: 1.5 }}>Your Early Warning Aspirating Detection request has been received and is now pending review.</p>
          <p style={{ fontSize: 13, color: C.t3, fontStyle: "italic", margin: "0 0 28px" }}>Su solicitud de detección de alerta temprana por aspiración ha sido recibida y está pendiente de revisión.</p>
          <button onClick={onComplete || onBack}
            style={{ padding: "12px 36px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", boxShadow: `0 4px 16px ${C.navy}25` }}>
            OK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit", padding: "4px 0", marginBottom: 16, transition: "color 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.color = C.navy} onMouseLeave={e => e.currentTarget.style.color = C.t3}>
        <Ic.ChevronLeft /> Back to Detection Systems
      </button>

      <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "32px 36px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#F59E0B12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔬</div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: 0 }}>Early Warning Aspirating Detection</h2>
            <p style={{ fontSize: 13, color: C.t2, margin: 0 }}>Aspirating detection systems</p>
          </div>
        </div>
        <p style={{ fontSize: 12, color: C.t3, fontStyle: "italic", margin: "0 0 4px", lineHeight: 1.5 }}>Please fill out the form as completely as possible to ensure an accurate system design.<br />Por favor complete el formulario lo más posible para un diseño preciso del sistema.</p>
        <p style={{ fontSize: 11, color: C.t3, margin: "0 0 24px" }}><span style={{ color: C.red }}>*</span> Required fields / Campos obligatorios</p>

        <Section title="Project Information" titleEs="Información del Proyecto" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TextField en="Project Name" es="Nombre del Proyecto" value={form.projectName} onChange={v => set("projectName", v)} placeholder="e.g. Data Center Early Warning" required error={errors.projectName} />
          <TextField en="Project Location" es="Ubicación del Proyecto" value={form.projectLocation} onChange={v => set("projectLocation", v)} placeholder="City, Country" required error={errors.projectLocation} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Label en="End Client" es="Cliente Final" required />
          <input value={form.endClient} onChange={e => set("endClient", e.target.value)} placeholder="Final client name" style={{ ...inputStyle, borderColor: errors.endClient ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur} />
          <p style={{ fontSize: 11, color: C.t3, fontStyle: "italic", margin: "4px 0 0" }}>Confidential — only used for project registration with the manufacturer.<br />Confidencial — solo se usa para el registro del proyecto con el fabricante.</p>
          {errors.endClient && <span style={{ fontSize: 11, color: C.red }}>{errors.endClient}</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TextField en="Estimated Closing Date" es="Fecha Estimada de Cierre" value={form.estimatedClosingDate} onChange={v => set("estimatedClosingDate", v)} type="date" required error={errors.estimatedClosingDate} />
          <TextField en="Estimated Execution Date" es="Fecha Estimada de Ejecución" value={form.estimatedExecDate} onChange={v => set("estimatedExecDate", v)} type="date" required error={errors.estimatedExecDate} />
        </div>

        <Section title="Performance Requirements" titleEs="Requisitos de Desempeño" />
        <SelectField en="Detection Objective" es="Objetivo de Detección" value={form.detectionObjective} onChange={v => set("detectionObjective", v)} options={["Very Early Warning (Pre-smoke)", "Early Warning", "Suppression system activation", "General fire detection", "Complement to spot detection"]} required error={errors.detectionObjective} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TextField en="Required Sensitivity (OBS %/ft)" es="Sensibilidad Requerida (si se especifica)" value={form.sensitivity} onChange={v => set("sensitivity", v)} placeholder="If specified in project" />
          <SelectField en="Required Design Standard" es="Normativa de Diseño Requerida" value={form.designStandard} onChange={v => set("designStandard", v)} options={["NFPA 72", "NFPA 76", "EN 54-20", "Other"]} required error={errors.designStandard} />
        </div>

        <Section title="Risk Information" titleEs="Información del Riesgo" />
        <SelectField en="Risk Area" es="Área de Riesgo" value={form.riskArea} onChange={v => set("riskArea", v)} options={["Data Center", "Telecom Room", "Electrical Room", "Warehouse / Storage", "Cold Room", "Terminal / Airport", "Atrium / Conference Center / Mall", "Archive / Museum", "Industrial / Factory", "Other"]} required error={errors.riskArea} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <SelectField en="Risk Type" es="Tipo de Riesgo" value={form.riskType} onChange={v => set("riskType", v)} options={["Class A", "Class B", "Class C", "Class D"]} required error={errors.riskType} />
          <TextField en="Estimated Temperature" es="Temperatura Estimada" value={form.estimatedTemp} onChange={v => set("estimatedTemp", v)} placeholder="e.g. 72" />
        </div>
        <SelectField en="Environment Type" es="Tipo de Ambiente" value={form.environment} onChange={v => set("environment", v)} options={["Clean", "Dusty", "Industrial / dirty", "Cold (below 5°C / 40°F)", "High humidity", "Corrosive"]} required error={errors.environment} />
        <SelectField en="Is there an HVAC system?" es="¿Hay sistema de HVAC?" value={form.hasHVAC} onChange={v => set("hasHVAC", v)} options={["Yes / Sí", "No"]} required error={errors.hasHVAC} />
        {hasHVAC && (
          <TextField en="Air Supply Location" es="Ubicación del suministro de aire" value={form.hvacAirSupplyLocation} onChange={v => set("hvacAirSupplyLocation", v)} placeholder="Describe location" />
        )}

        <Section title="Area Dimensions" titleEs="Dimensiones del Área" />
        <UnitSelector value={form.unitSystem} onChange={v => set("unitSystem", v)} required error={errors.unitSystem} />
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.t1, margin: "0 0 12px" }}>Main Environment <span style={{ fontWeight: 400, color: C.t3, fontStyle: "italic" }}>/ Ambiente Principal</span> <span style={{ color: C.red }}>*</span></h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" }}>
          <TextField en="Length" es="Largo" value={form.mainLength} onChange={v => set("mainLength", v)} placeholder="e.g. 12" required error={errors.mainLength} />
          <TextField en="Width" es="Ancho" value={form.mainWidth} onChange={v => set("mainWidth", v)} placeholder="e.g. 8" required error={errors.mainWidth} />
          <TextField en="Height" es="Alto" value={form.mainHeight} onChange={v => set("mainHeight", v)} placeholder="e.g. 3.5" required error={errors.mainHeight} />
        </div>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.t1, margin: "16px 0 12px" }}>Raised Floor (if applicable) <span style={{ fontWeight: 400, color: C.t3, fontStyle: "italic" }}>/ Piso Falso</span></h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" }}>
          <TextField en="Length" es="Largo" value={form.falseFloorLength} onChange={v => set("falseFloorLength", v)} />
          <TextField en="Width" es="Ancho" value={form.falseFloorWidth} onChange={v => set("falseFloorWidth", v)} />
          <TextField en="Height" es="Alto" value={form.falseFloorHeight} onChange={v => set("falseFloorHeight", v)} />
        </div>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.t1, margin: "16px 0 12px" }}>False Ceiling (if applicable) <span style={{ fontWeight: 400, color: C.t3, fontStyle: "italic" }}>/ Techo Falso</span></h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" }}>
          <TextField en="Length" es="Largo" value={form.falseCeilingLength} onChange={v => set("falseCeilingLength", v)} />
          <TextField en="Width" es="Ancho" value={form.falseCeilingWidth} onChange={v => set("falseCeilingWidth", v)} />
          <TextField en="Height" es="Alto" value={form.falseCeilingHeight} onChange={v => set("falseCeilingHeight", v)} />
        </div>
        <SelectField en="Ceiling Type" es="Tipo de Techo" value={form.ceilingType} onChange={v => set("ceilingType", v)} options={["Flat", "Sloped", "Racks / Warehouse type", "Exposed structure"]} />
        <MultiSelectField en="Areas to protect" es="Se requiere proteger" value={form.protectionAreas} onChange={v => set("protectionAreas", v)} options={["Main environment only", "Above false ceiling", "Below raised floor", "Inside cabinets", "Return plenum"]} required error={errors.protectionAreas} />

        <Section title="Fire Panel Interface" titleEs="Interfaz con Panel de Incendio" />
        <SelectField en="Include fire panel?" es="¿Incluir panel de incendio?" value={form.needsPanel} onChange={v => set("needsPanel", v)} options={["Yes / Sí", "No"]} required error={errors.needsPanel} />
        {needsPanel && (
          <SelectField en="Panel Type" es="Tipo de Panel" value={form.panelType} onChange={v => set("panelType", v)} options={["Conventional Discharge / Convencional de Descarga", "Addressable / Direccionable"]} required error={errors.panelType} />
        )}

        {needsPanel && (<>
          <Section title="Devices & Accessories (enter quantities)" titleEs="Dispositivos y Accesorios (indicar cantidades)" />
          <p style={{ fontSize: 12, color: C.t3, fontStyle: "italic", margin: "-12px 0 16px" }}>If none desired, enter 0 as the quantity. / Si no se requiere, ingrese 0 como cantidad.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0 16px" }}>
            <TextField en="Pull Stations" es="Estaciones Manuales" value={form.pullStations} onChange={v => set("pullStations", v)} placeholder="Qty" required error={errors.pullStations} />
            <TextField en="Abort Switch" es="Switch de Aborto" value={form.abortSwitch} onChange={v => set("abortSwitch", v)} placeholder="Qty" required error={errors.abortSwitch} />
            <TextField en="Suppression Release" es="Estación de Descarga" value={form.suppressionRelease} onChange={v => set("suppressionRelease", v)} placeholder="Qty" required error={errors.suppressionRelease} />
            <TextField en="Maint. Switch" es="Switch Mantenimiento" value={form.maintenanceSwitch} onChange={v => set("maintenanceSwitch", v)} placeholder="Qty" required error={errors.maintenanceSwitch} />
            <TextField en="Strobe / Horns" es="Estrobos / Sirenas" value={form.strobeHorns} onChange={v => set("strobeHorns", v)} placeholder="Qty" required error={errors.strobeHorns} />
            <TextField en="Fire Bell" es="Campana" value={form.fireBell} onChange={v => set("fireBell", v)} placeholder="Qty" required error={errors.fireBell} />
            <TextField en="Mini Monitor Modules" es="Mini Módulos de Monitoreo" value={form.monitoringModules} onChange={v => set("monitoringModules", v)} placeholder="Qty" required error={errors.monitoringModules} />
            <TextField en="Relay Modules" es="Módulos de Relay" value={form.relayModules} onChange={v => set("relayModules", v)} placeholder="Qty" required error={errors.relayModules} />
          </div>
        </>)}

        <Section title="Suppression System" titleEs="Sistema de Supresión" />
        <SelectField en="Do you need a suppression system?" es="¿Necesita un sistema de supresión?" value={form.needsSuppression} onChange={v => set("needsSuppression", v)} options={["Yes / Sí", "No"]} />
        {form.needsSuppression === "Yes / Sí" && (
          <div style={{ padding: "12px 16px", borderRadius: 8, background: C.blueLight, border: `1px solid ${C.blue}30` }}>
            <p style={{ fontSize: 13, color: C.navy, margin: 0 }}>Please also complete a suppression system request form for the corresponding system type.</p>
            <p style={{ fontSize: 12, color: C.t3, fontStyle: "italic", margin: "4px 0 0" }}>Por favor también complete un formulario de solicitud de sistema de supresión para el tipo de sistema correspondiente.</p>
          </div>
        )}

        <Section title="Additional Notes" titleEs="Notas Adicionales" />
        <TextField en="Notes / Comments" es="Notas / Comentarios" value={form.additionalNotes} onChange={v => set("additionalNotes", v)} type="textarea" placeholder="Any additional information about the project..." />

        <Section title="Reference Documents" titleEs="Documentos de Referencia" />
        <FileUpload files={files} onChange={setFiles} />

        {Object.keys(errors).length > 0 && (
          <div style={{ marginTop: 24, padding: "12px 16px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
            <p style={{ fontSize: 13, color: C.red, fontWeight: 600, margin: "0 0 4px" }}>Please complete all required fields before submitting.</p>
            <p style={{ fontSize: 12, color: C.red, margin: 0, fontStyle: "italic" }}>Por favor complete todos los campos obligatorios antes de enviar.</p>
          </div>
        )}

        <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onBack} style={{ padding: "12px 28px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t2, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting}
            style={{ padding: "12px 32px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 15, fontWeight: 600, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: `0 4px 16px ${C.navy}25`, opacity: submitting ? 0.75 : 1 }}>
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}
